import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Checkbox } from "../components/ui/checkbox";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { ScrollArea } from "../components/ui/scroll-area";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  CheckCircle,
  Clock,
  Users,
  Plus,
  Bell,
  LogOut,
  MoreVertical,
  Trash2,
  Edit,
  TrendingUp,
  ChevronDown,
  Menu,
  X,
  ChevronRight,
  User,
  LayoutDashboard,
  UserCog,
  Repeat,
  Play,
  CalendarIcon,
  Phone,
  FolderKanban,
  ListTodo,
  Ban,
  UserCheck,
  AlertTriangle
} from "lucide-react";
import { format } from "date-fns";
import { cn } from "../lib/utils";
import { QACallsModule } from "./QACallsModule";

export const AdminDashboard = ({ user, setUser }) => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("dashboard");

  // User management state
  const [userToDelete, setUserToDelete] = useState(null);
  const [userToSuspend, setUserToSuspend] = useState(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showSuspendDialog, setShowSuspendDialog] = useState(false);
  
  // Project assignment state
  const [qaProjects, setQaProjects] = useState([]);
  const [showProjectAssignDialog, setShowProjectAssignDialog] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [selectedProjectIds, setSelectedProjectIds] = useState([]);

  // Task creation state
  const [showTaskDialog, setShowTaskDialog] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [taskForm, setTaskForm] = useState({
    title: "",
    description: "",
    priority: "medium",
    assigned_to: "",
    task_date: null,
    start_time: "09:00",
    duration: 60,
    is_recurring: false,
    repeat_day: "Monday"
  });

  // Recurring schedule state
  const [recurringSchedules, setRecurringSchedules] = useState([]);
  const [showScheduleDialog, setShowScheduleDialog] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [selectedDay, setSelectedDay] = useState("Monday");
  const [scheduleForm, setScheduleForm] = useState({
    day_of_week: "Monday",
    time_slot: "",
    title: "",
    description: "",
    priority: "medium",
    assigned_to: ""
  });

  // Fetch data
  const fetchData = useCallback(async () => {
    try {
      const [statsRes, tasksRes, employeesRes, notificationsRes, schedulesRes] = await Promise.all([
        axios.get(`${API}/stats`, { withCredentials: true }),
        axios.get(`${API}/tasks`, { withCredentials: true }),
        axios.get(`${API}/users/employees`, { withCredentials: true }),
        axios.get(`${API}/notifications`, { withCredentials: true }),
        axios.get(`${API}/recurring-schedules`, { withCredentials: true })
      ]);
      setStats(statsRes.data);
      setTasks(tasksRes.data);
      setEmployees(employeesRes.data);
      setNotifications(notificationsRes.data);
      setRecurringSchedules(schedulesRes.data);
      
      // Fetch QA projects for assignment
      try {
        const projectsRes = await axios.get(`${API}/qa/projects`, { withCredentials: true });
        setQaProjects(projectsRes.data);
      } catch (e) {
        console.log("Could not fetch QA projects");
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      if (error.response?.status === 401) {
        navigate("/", { replace: true });
      }
    } finally {
      setIsLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // WebSocket for real-time notifications
  useEffect(() => {
    if (!user?.user_id) return;

    const wsUrl = process.env.REACT_APP_BACKEND_URL.replace(/^http/, 'ws');
    const ws = new WebSocket(`${wsUrl}/ws/${user.user_id}`);

    ws.onopen = () => {
      console.log("WebSocket connected");
    };

    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);
      toast.info(data.message);
      fetchData();
    };

    ws.onclose = () => {
      console.log("WebSocket disconnected");
    };

    // Ping to keep alive
    const pingInterval = setInterval(() => {
      if (ws.readyState === WebSocket.OPEN) {
        ws.send("ping");
      }
    }, 30000);

    return () => {
      clearInterval(pingInterval);
      ws.close();
    };
  }, [user?.user_id, fetchData]);

  // Logout
  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      navigate("/", { replace: true });
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  // Mark notification as read
  const markNotificationRead = async (notificationId) => {
    try {
      await axios.patch(`${API}/notifications/${notificationId}/read`, {}, { withCredentials: true });
      fetchData();
    } catch (error) {
      console.error("Error marking notification read:", error);
    }
  };

  // Suspend user
  const handleSuspendUser = async (userId) => {
    try {
      await axios.patch(`${API}/users/${userId}/suspend`, {}, { withCredentials: true });
      toast.success("User suspended successfully");
      setShowSuspendDialog(false);
      setUserToSuspend(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to suspend user");
    }
  };

  // Activate user
  const handleActivateUser = async (userId) => {
    try {
      await axios.patch(`${API}/users/${userId}/activate`, {}, { withCredentials: true });
      toast.success("User activated successfully");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to activate user");
    }
  };

  // Delete user
  const handleDeleteUser = async (userId) => {
    try {
      await axios.delete(`${API}/users/${userId}`, { withCredentials: true });
      toast.success("User deleted successfully");
      setShowDeleteDialog(false);
      setUserToDelete(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete user");
    }
  };

  // Update user projects
  const handleUpdateUserProjects = async () => {
    if (!selectedEmployee) return;
    try {
      await axios.patch(
        `${API}/users/${selectedEmployee.user_id}/projects`,
        { project_ids: selectedProjectIds },
        { withCredentials: true }
      );
      toast.success("Projects assigned successfully");
      setShowProjectAssignDialog(false);
      setSelectedEmployee(null);
      setSelectedProjectIds([]);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to assign projects");
    }
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  // Update user role
  const handleUpdateRole = async (userId, newRole) => {
    try {
      await axios.patch(`${API}/users/${userId}/role`, { role: newRole }, { withCredentials: true });
      toast.success("Role updated");
      fetchData();
    } catch (error) {
      toast.error("Failed to update role");
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-muted-foreground">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-secondary/30" data-testid="admin-dashboard">
      {/* Mobile sidebar overlay */}
      <AnimatePresence>
        {sidebarOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 mobile-overlay lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}
      </AnimatePresence>

      {/* Sidebar */}
      <aside className={cn(
        "fixed left-0 top-0 z-50 h-full w-64 bg-white border-r border-border transition-transform duration-300 lg:translate-x-0",
        sidebarOpen ? "translate-x-0" : "-translate-x-full"
      )}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center gap-2 px-6 py-5 border-b border-border">
            <div className="w-8 h-8 rounded-lg bg-[#0066CC] flex items-center justify-center">
              <Phone className="w-4 h-4 text-white" />
            </div>
            <span className="text-lg font-heading font-bold text-foreground">Lexia</span>
            <button 
              className="ml-auto lg:hidden p-1 rounded-md hover:bg-muted"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-6 space-y-1">
            <button
              onClick={() => setActiveTab("dashboard")}
              className={cn(
                "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors",
                activeTab === "dashboard" ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-muted hover:text-foreground"
              )}
              data-testid="nav-dashboard"
            >
              <LayoutDashboard className="w-5 h-5" />
              Dashboard
            </button>
            <button
              onClick={() => setActiveTab("tasks")}
              className={cn(
                "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors",
                activeTab === "tasks" ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-muted hover:text-foreground"
              )}
              data-testid="nav-tasks"
            >
              <ListTodo className="w-5 h-5" />
              Tasks
            </button>
            <button
              onClick={() => setActiveTab("schedules")}
              className={cn(
                "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors",
                activeTab === "schedules" ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-muted hover:text-foreground"
              )}
              data-testid="nav-schedules"
            >
              <Repeat className="w-5 h-5" />
              Weekly Schedule
            </button>
            <button
              onClick={() => setActiveTab("qa_calls")}
              className={cn(
                "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors",
                activeTab === "qa_calls" ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-muted hover:text-foreground"
              )}
              data-testid="nav-qa-calls"
            >
              <Phone className="w-5 h-5" />
              QA Calls
            </button>
            <button
              onClick={() => setActiveTab("employees")}
              className={cn(
                "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors",
                activeTab === "employees" ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-muted hover:text-foreground"
              )}
              data-testid="nav-employees"
            >
              <Users className="w-5 h-5" />
              Employees
            </button>
          </nav>

          {/* User */}
          <div className="p-4 border-t border-border">
            <div className="flex items-center gap-3 px-2">
              <Avatar className="w-9 h-9">
                <AvatarImage src={user?.picture} alt={user?.name} />
                <AvatarFallback>{user?.name?.charAt(0)}</AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground truncate">{user?.name}</p>
                <p className="text-xs text-muted-foreground">Admin</p>
              </div>
              <Button variant="ghost" size="icon" onClick={handleLogout} data-testid="logout-btn">
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64 min-h-screen">
        {/* Header */}
        <header className="sticky top-0 z-30 bg-white/80 backdrop-blur-xl border-b border-border">
          <div className="flex items-center justify-between px-4 lg:px-8 py-4">
            <div className="flex items-center gap-4">
              <button 
                className="lg:hidden p-2 rounded-md hover:bg-muted"
                onClick={() => setSidebarOpen(true)}
              >
                <Menu className="w-5 h-5" />
              </button>
              <h1 className="text-xl font-heading font-semibold text-foreground">
                {activeTab === "dashboard" && "Dashboard"}
                {activeTab === "tasks" && "Task Management"}
                {activeTab === "schedules" && "Weekly Recurring Schedule"}
                {activeTab === "qa_calls" && "QA Calls"}
                {activeTab === "employees" && "Team Members"}
              </h1>
            </div>

            <div className="flex items-center gap-3">
              {/* Notifications */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="relative" data-testid="notifications-btn">
                    <Bell className="w-5 h-5" />
                    {unreadCount > 0 && (
                      <span className="absolute -top-1 -right-1 w-5 h-5 bg-destructive text-destructive-foreground text-xs rounded-full flex items-center justify-center notification-pulse">
                        {unreadCount}
                      </span>
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-80">
                  <DropdownMenuLabel>Notifications</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <ScrollArea className="h-64">
                    {notifications.length === 0 ? (
                      <div className="py-8 text-center text-muted-foreground text-sm">
                        No notifications
                      </div>
                    ) : (
                      notifications.slice(0, 10).map((notification) => (
                        <DropdownMenuItem
                          key={notification.notification_id}
                          className={cn("flex flex-col items-start gap-1 p-3 cursor-pointer", !notification.read && "bg-primary/5")}
                          onClick={() => markNotificationRead(notification.notification_id)}
                        >
                          <p className="text-sm">{notification.message}</p>
                          <p className="text-xs text-muted-foreground">
                            {new Date(notification.created_at).toLocaleString()}
                          </p>
                        </DropdownMenuItem>
                      ))
                    )}
                  </ScrollArea>
                </DropdownMenuContent>
              </DropdownMenu>

              {/* User menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2" data-testid="user-menu-btn">
                    <Avatar className="w-8 h-8">
                      <AvatarImage src={user?.picture} alt={user?.name} />
                      <AvatarFallback>{user?.name?.charAt(0)}</AvatarFallback>
                    </Avatar>
                    <ChevronDown className="w-4 h-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuLabel>{user?.name}</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate("/dashboard")}>
                    <User className="w-4 h-4 mr-2" />
                    Employee View
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout}>
                    <LogOut className="w-4 h-4 mr-2" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        {/* Content Area */}
        <div className="p-4 lg:p-8">
          {/* Dashboard Tab */}
          {activeTab === "dashboard" && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              className="space-y-6"
            >
              {/* Stats Grid */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <Card className="shadow-card">
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">Weekly Schedules</p>
                        <p className="text-2xl font-heading font-bold text-foreground mt-1">{recurringSchedules.length || 0}</p>
                      </div>
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                        <Repeat className="w-5 h-5 text-primary" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="shadow-card">
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">Pending Tasks</p>
                        <p className="text-2xl font-heading font-bold text-amber-600 mt-1">{stats?.pending_tasks || 0}</p>
                      </div>
                      <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                        <Clock className="w-5 h-5 text-amber-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="shadow-card">
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">In Progress</p>
                        <p className="text-2xl font-heading font-bold text-blue-600 mt-1">{stats?.in_progress_tasks || 0}</p>
                      </div>
                      <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                        <TrendingUp className="w-5 h-5 text-blue-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="shadow-card">
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">Completed</p>
                        <p className="text-2xl font-heading font-bold text-green-600 mt-1">{stats?.completed_tasks || 0}</p>
                      </div>
                      <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                        <CheckCircle className="w-5 h-5 text-green-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Schedule by Day & Priority */}
              <div className="grid lg:grid-cols-2 gap-6">
                <Card className="shadow-card">
                  <CardHeader>
                    <CardTitle className="text-lg font-heading">Schedule by Day</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"].map(day => {
                        const count = recurringSchedules.filter(s => s.day_of_week === day).length;
                        return (
                          <div key={day} className="flex items-center justify-between">
                            <span className="text-sm">{day}</span>
                            <span className="font-semibold">{count} tasks</span>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>

                <Card className="shadow-card">
                  <CardHeader>
                    <CardTitle className="text-lg font-heading">Priority Breakdown</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full bg-red-500" />
                          <span className="text-sm">High Priority</span>
                        </div>
                        <span className="font-semibold">{recurringSchedules.filter(s => s.priority === "high").length}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full bg-amber-500" />
                          <span className="text-sm">Medium Priority</span>
                        </div>
                        <span className="font-semibold">{recurringSchedules.filter(s => s.priority === "medium").length}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full bg-green-500" />
                          <span className="text-sm">Low Priority</span>
                        </div>
                        <span className="font-semibold">{recurringSchedules.filter(s => s.priority === "low").length}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Quick Actions */}
              <div className="flex flex-wrap gap-3">
                <Button
                  onClick={() => setActiveTab("schedules")}
                  className="bg-primary text-primary-foreground hover:bg-primary/90"
                  data-testid="manage-schedule-btn"
                >
                  <Repeat className="w-4 h-4 mr-2" />
                  Manage Weekly Schedule
                </Button>
                <Button
                  variant="outline"
                  onClick={async () => {
                    try {
                      const res = await axios.post(`${API}/recurring-schedules/generate-weekly-tasks`, {}, { withCredentials: true });
                      toast.success(res.data.message);
                      fetchData();
                    } catch (error) {
                      toast.error("Failed to generate weekly tasks");
                    }
                  }}
                  data-testid="generate-tasks-btn-dashboard"
                >
                  <Play className="w-4 h-4 mr-2" />
                  Generate This Week's Tasks
                </Button>
              </div>

              {/* Weekly Schedule Overview */}
              <Card className="shadow-card">
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="text-lg font-heading">Weekly Schedule Overview</CardTitle>
                  <Button variant="ghost" size="sm" onClick={() => setActiveTab("schedules")}>
                    Manage Schedule <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {recurringSchedules.slice(0, 5).map((schedule) => (
                      <div key={schedule.schedule_id} className="flex items-center justify-between p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors">
                        <div className="flex items-center gap-3">
                          <div className={cn(
                            "w-2 h-2 rounded-full",
                            schedule.priority === "high" && "bg-red-500",
                            schedule.priority === "medium" && "bg-amber-500",
                            schedule.priority === "low" && "bg-green-500"
                          )} />
                          <div>
                            <p className="text-sm font-medium">{schedule.title}</p>
                            <p className="text-xs text-muted-foreground">{schedule.day_of_week} • {schedule.time_slot}</p>
                          </div>
                        </div>
                        <Badge variant="outline" className={cn(
                          "text-xs",
                          schedule.assigned_to_name ? "bg-blue-100 text-blue-700" : "bg-gray-100 text-gray-600"
                        )}>
                          {schedule.assigned_to_name || "Unassigned"}
                        </Badge>
                      </div>
                    ))}
                    {recurringSchedules.length === 0 && (
                      <div className="py-8 text-center text-muted-foreground">
                        No weekly schedules yet. Set up your recurring tasks!
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* Employees Tab */}
          {activeTab === "employees" && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              className="space-y-6"
            >
              <div className="flex items-center justify-between">
                <p className="text-muted-foreground">{employees.length} team members</p>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {employees.map((employee) => (
                  <Card key={employee.user_id} className={cn(
                    "shadow-card",
                    employee.status === "suspended" && "opacity-60 border-amber-300"
                  )} data-testid={`employee-card-${employee.user_id}`}>
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                          <Avatar className={cn("w-12 h-12", employee.status === "suspended" && "grayscale")}>
                            <AvatarImage src={employee.picture} alt={employee.name} />
                            <AvatarFallback>{employee.name?.charAt(0)}</AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="font-medium text-foreground">{employee.name}</p>
                            <p className="text-sm text-muted-foreground">{employee.email}</p>
                          </div>
                        </div>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" data-testid={`employee-menu-${employee.user_id}`}>
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuLabel>Change Role</DropdownMenuLabel>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem 
                              onClick={() => handleUpdateRole(employee.user_id, "admin")}
                              disabled={employee.role === "admin" || employee.status === "suspended"}
                            >
                              <UserCog className="w-4 h-4 mr-2" />
                              Make Admin
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              onClick={() => handleUpdateRole(employee.user_id, "qa_agent")}
                              disabled={employee.role === "qa_agent" || employee.status === "suspended"}
                            >
                              <Phone className="w-4 h-4 mr-2" />
                              Make QA Agent
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              onClick={() => handleUpdateRole(employee.user_id, "employee")}
                              disabled={employee.role === "employee" || employee.status === "suspended"}
                            >
                              <User className="w-4 h-4 mr-2" />
                              Make Employee
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuLabel>QA Projects</DropdownMenuLabel>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem 
                              onClick={() => {
                                setSelectedEmployee(employee);
                                setSelectedProjectIds(employee.assigned_projects || []);
                                setShowProjectAssignDialog(true);
                              }}
                              disabled={employee.status === "suspended"}
                            >
                              <FolderKanban className="w-4 h-4 mr-2" />
                              Assign Projects
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuLabel>Account Actions</DropdownMenuLabel>
                            <DropdownMenuSeparator />
                            {employee.status === "suspended" ? (
                              <DropdownMenuItem 
                                onClick={() => handleActivateUser(employee.user_id)}
                                className="text-green-600 focus:text-green-600"
                              >
                                <UserCheck className="w-4 h-4 mr-2" />
                                Activate User
                              </DropdownMenuItem>
                            ) : (
                              <DropdownMenuItem 
                                onClick={() => {
                                  setUserToSuspend(employee);
                                  setShowSuspendDialog(true);
                                }}
                                disabled={employee.user_id === user?.user_id}
                                className="text-amber-600 focus:text-amber-600"
                              >
                                <Ban className="w-4 h-4 mr-2" />
                                Suspend User
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem 
                              onClick={() => {
                                setUserToDelete(employee);
                                setShowDeleteDialog(true);
                              }}
                              disabled={employee.user_id === user?.user_id}
                              className="text-destructive focus:text-destructive"
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Delete User
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                      <div className="mt-4 flex gap-2">
                        <Badge variant="outline" className={cn(
                          employee.role === "admin" ? "bg-primary/10 text-primary border-primary/20" : "bg-muted"
                        )}>
                          {employee.role}
                        </Badge>
                        {employee.status === "suspended" && (
                          <Badge variant="outline" className="bg-amber-100 text-amber-700 border-amber-200">
                            Suspended
                          </Badge>
                        )}
                        {employee.assigned_projects?.length > 0 && (
                          <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                            <FolderKanban className="w-3 h-3 mr-1" />
                            {employee.assigned_projects.length} project(s)
                          </Badge>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </motion.div>
          )}

          {/* QA Calls Tab */}
          {activeTab === "qa_calls" && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <QACallsModule user={user} />
            </motion.div>
          )}

          {/* Weekly Schedule Tab */}
          {activeTab === "schedules" && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              className="space-y-6"
            >
              {/* Actions */}
              <div className="flex flex-col sm:flex-row gap-4 justify-between">
                <div className="flex items-center gap-3">
                  <Select value={selectedDay} onValueChange={setSelectedDay}>
                    <SelectTrigger className="w-40" data-testid="select-day">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Monday">Monday</SelectItem>
                      <SelectItem value="Tuesday">Tuesday</SelectItem>
                      <SelectItem value="Wednesday">Wednesday</SelectItem>
                      <SelectItem value="Thursday">Thursday</SelectItem>
                      <SelectItem value="Friday">Friday</SelectItem>
                      <SelectItem value="Saturday">Saturday</SelectItem>
                      <SelectItem value="Sunday">Sunday</SelectItem>
                    </SelectContent>
                  </Select>
                  <span className="text-sm text-muted-foreground">
                    {recurringSchedules.filter(s => s.day_of_week === selectedDay).length} scheduled tasks
                  </span>
                </div>
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={async () => {
                      try {
                        const res = await axios.post(`${API}/recurring-schedules/seed-monday`, {}, { withCredentials: true });
                        toast.success(res.data.message);
                        fetchData();
                      } catch (error) {
                        toast.error("Failed to seed Monday schedule");
                      }
                    }}
                    data-testid="seed-monday-btn"
                  >
                    <CalendarIcon className="w-4 h-4 mr-2" />
                    Setup Monday Template
                  </Button>
                  <Button
                    variant="outline"
                    onClick={async () => {
                      try {
                        const res = await axios.post(`${API}/recurring-schedules/generate-weekly-tasks`, {}, { withCredentials: true });
                        toast.success(res.data.message);
                        fetchData();
                      } catch (error) {
                        toast.error("Failed to generate weekly tasks");
                      }
                    }}
                    data-testid="generate-tasks-btn"
                  >
                    <Play className="w-4 h-4 mr-2" />
                    Generate This Week's Tasks
                  </Button>
                  <Button
                    onClick={() => {
                      setEditingSchedule(null);
                      setScheduleForm({
                        day_of_week: selectedDay,
                        time_slot: "",
                        title: "",
                        description: "",
                        priority: "medium",
                        assigned_to: ""
                      });
                      setShowScheduleDialog(true);
                    }}
                    className="bg-primary text-primary-foreground hover:bg-primary/90"
                    data-testid="add-schedule-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Schedule
                  </Button>
                </div>
              </div>

              {/* Info Card */}
              <Card className="shadow-card bg-blue-50 border-blue-200">
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <Repeat className="w-5 h-5 text-blue-600 mt-0.5" />
                    <div>
                      <p className="font-medium text-blue-900">Recurring Weekly Tasks</p>
                      <p className="text-sm text-blue-700">
                        These schedules repeat every week. Click "Generate This Week's Tasks" to create tasks for the current week.
                        Tasks will be created with deadlines set for their scheduled day.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Schedule Table */}
              <Card className="shadow-card">
                <CardHeader>
                  <CardTitle className="text-lg font-heading">{selectedDay} Schedule</CardTitle>
                </CardHeader>
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-muted/50">
                        <TableHead>Time Slot</TableHead>
                        <TableHead>Task</TableHead>
                        <TableHead>Priority</TableHead>
                        <TableHead>Assigned To</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {recurringSchedules
                        .filter(s => s.day_of_week === selectedDay)
                        .sort((a, b) => a.time_slot.localeCompare(b.time_slot))
                        .map((schedule) => (
                          <TableRow key={schedule.schedule_id} className="table-row-hover" data-testid={`schedule-row-${schedule.schedule_id}`}>
                            <TableCell>
                              <span className="font-mono text-sm">{schedule.time_slot}</span>
                            </TableCell>
                            <TableCell>
                              <div>
                                <p className="font-medium">{schedule.title}</p>
                                {schedule.description && (
                                  <p className="text-xs text-muted-foreground truncate max-w-xs">{schedule.description}</p>
                                )}
                              </div>
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline" className={cn(
                                "text-xs",
                                schedule.priority === "high" && "priority-high",
                                schedule.priority === "medium" && "priority-medium",
                                schedule.priority === "low" && "priority-low"
                              )}>
                                {schedule.priority}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              {schedule.assigned_to_name ? (
                                <div className="flex items-center gap-2">
                                  <Avatar className="w-6 h-6">
                                    <AvatarFallback className="text-xs">{schedule.assigned_to_name.charAt(0)}</AvatarFallback>
                                  </Avatar>
                                  <span className="text-sm">{schedule.assigned_to_name}</span>
                                </div>
                              ) : (
                                <span className="text-sm text-muted-foreground">Unassigned</span>
                              )}
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline" className={cn(
                                "text-xs",
                                schedule.is_active ? "bg-green-100 text-green-700 border-green-200" : "bg-gray-100 text-gray-600"
                              )}>
                                {schedule.is_active ? "Active" : "Inactive"}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-right">
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="icon" data-testid={`schedule-menu-${schedule.schedule_id}`}>
                                    <MoreVertical className="w-4 h-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem onClick={() => {
                                    setEditingSchedule(schedule);
                                    setScheduleForm({
                                      day_of_week: schedule.day_of_week,
                                      time_slot: schedule.time_slot,
                                      title: schedule.title,
                                      description: schedule.description || "",
                                      priority: schedule.priority,
                                      assigned_to: schedule.assigned_to || ""
                                    });
                                    setShowScheduleDialog(true);
                                  }}>
                                    <Edit className="w-4 h-4 mr-2" />
                                    Edit
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={async () => {
                                    try {
                                      await axios.patch(`${API}/recurring-schedules/${schedule.schedule_id}`, 
                                        { is_active: !schedule.is_active }, 
                                        { withCredentials: true }
                                      );
                                      toast.success(schedule.is_active ? "Schedule deactivated" : "Schedule activated");
                                      fetchData();
                                    } catch (error) {
                                      toast.error("Failed to update schedule");
                                    }
                                  }}>
                                    {schedule.is_active ? (
                                      <>
                                        <X className="w-4 h-4 mr-2" />
                                        Deactivate
                                      </>
                                    ) : (
                                      <>
                                        <CheckCircle className="w-4 h-4 mr-2" />
                                        Activate
                                      </>
                                    )}
                                  </DropdownMenuItem>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem 
                                    className="text-destructive focus:text-destructive"
                                    onClick={async () => {
                                      try {
                                        await axios.delete(`${API}/recurring-schedules/${schedule.schedule_id}`, { withCredentials: true });
                                        toast.success("Schedule deleted");
                                        fetchData();
                                      } catch (error) {
                                        toast.error("Failed to delete schedule");
                                      }
                                    }}
                                  >
                                    <Trash2 className="w-4 h-4 mr-2" />
                                    Delete
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </TableCell>
                          </TableRow>
                        ))}
                      {recurringSchedules.filter(s => s.day_of_week === selectedDay).length === 0 && (
                        <TableRow>
                          <TableCell colSpan={6} className="text-center py-12 text-muted-foreground">
                            No recurring tasks scheduled for {selectedDay}
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              </Card>
            </motion.div>
          )}

          {/* Tasks Tab */}
          {activeTab === "tasks" && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              className="space-y-6"
            >
              {/* Actions */}
              <div className="flex flex-col sm:flex-row gap-4 justify-between">
                <p className="text-muted-foreground">{tasks.length} total tasks</p>
                <Button
                  onClick={() => {
                    setEditingTask(null);
                    setTaskForm({
                      title: "",
                      description: "",
                      priority: "medium",
                      assigned_to: "",
                      task_date: null,
                      start_time: "09:00",
                      duration: 60,
                      is_recurring: false,
                      repeat_day: "Monday"
                    });
                    setShowTaskDialog(true);
                  }}
                  className="bg-primary text-primary-foreground hover:bg-primary/90"
                  data-testid="add-task-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create Task
                </Button>
              </div>

              {/* Tasks Table */}
              <Card className="shadow-card">
                <CardHeader>
                  <CardTitle className="text-lg font-heading">All Tasks</CardTitle>
                </CardHeader>
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-muted/50">
                        <TableHead>Task</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead>Time</TableHead>
                        <TableHead>Duration</TableHead>
                        <TableHead>Priority</TableHead>
                        <TableHead>Assigned To</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {tasks
                        .sort((a, b) => {
                          // Sort by date, then by time
                          if (a.task_date && b.task_date) {
                            if (a.task_date !== b.task_date) return a.task_date.localeCompare(b.task_date);
                          }
                          if (a.start_time && b.start_time) {
                            return a.start_time.localeCompare(b.start_time);
                          }
                          return 0;
                        })
                        .map((task) => (
                          <TableRow key={task.task_id} className="table-row-hover" data-testid={`task-row-${task.task_id}`}>
                            <TableCell>
                              <div>
                                <div className="flex items-center gap-2">
                                  <p className="font-medium">{task.title}</p>
                                  {task.is_recurring && (
                                    <Badge variant="outline" className="text-xs bg-blue-50 text-blue-600 border-blue-200">
                                      <Repeat className="w-3 h-3 mr-1" />
                                      {task.repeat_day}
                                    </Badge>
                                  )}
                                </div>
                                {task.description && (
                                  <p className="text-xs text-muted-foreground truncate max-w-xs">{task.description}</p>
                                )}
                              </div>
                            </TableCell>
                            <TableCell>
                              {task.task_date ? (
                                <span className="text-sm">{format(new Date(task.task_date + "T00:00:00"), "MMM d, yyyy")}</span>
                              ) : (
                                <span className="text-sm text-muted-foreground">-</span>
                              )}
                            </TableCell>
                            <TableCell>
                              {task.start_time ? (
                                <span className="font-mono text-sm">{task.start_time}</span>
                              ) : (
                                <span className="text-sm text-muted-foreground">-</span>
                              )}
                            </TableCell>
                            <TableCell>
                              {task.duration ? (
                                <span className="text-sm">{task.duration} min</span>
                              ) : (
                                <span className="text-sm text-muted-foreground">-</span>
                              )}
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline" className={cn(
                                "text-xs",
                                task.priority === "high" && "priority-high",
                                task.priority === "medium" && "priority-medium",
                                task.priority === "low" && "priority-low"
                              )}>
                                {task.priority}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              {task.assigned_to_name ? (
                                <div className="flex items-center gap-2">
                                  <Avatar className="w-6 h-6">
                                    <AvatarFallback className="text-xs">{task.assigned_to_name.charAt(0)}</AvatarFallback>
                                  </Avatar>
                                  <span className="text-sm">{task.assigned_to_name}</span>
                                </div>
                              ) : (
                                <span className="text-sm text-muted-foreground">Unassigned</span>
                              )}
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline" className={cn(
                                "text-xs",
                                task.status === "pending" && "bg-amber-100 text-amber-700 border-amber-200",
                                task.status === "in_progress" && "bg-blue-100 text-blue-700 border-blue-200",
                                task.status === "completed" && "bg-green-100 text-green-700 border-green-200"
                              )}>
                                {task.status?.replace("_", " ")}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-right">
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="icon" data-testid={`task-menu-${task.task_id}`}>
                                    <MoreVertical className="w-4 h-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem onClick={() => {
                                    setEditingTask(task);
                                    setTaskForm({
                                      title: task.title,
                                      description: task.description || "",
                                      priority: task.priority,
                                      assigned_to: task.assigned_to || "",
                                      task_date: task.task_date ? new Date(task.task_date + "T00:00:00") : null,
                                      start_time: task.start_time || "09:00",
                                      duration: task.duration || 60,
                                      is_recurring: task.is_recurring || false,
                                      repeat_day: task.repeat_day || "Monday"
                                    });
                                    setShowTaskDialog(true);
                                  }}>
                                    <Edit className="w-4 h-4 mr-2" />
                                    Edit
                                  </DropdownMenuItem>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem 
                                    className="text-destructive focus:text-destructive"
                                    onClick={async () => {
                                      try {
                                        await axios.delete(`${API}/tasks/${task.task_id}`, { withCredentials: true });
                                        toast.success("Task deleted");
                                        fetchData();
                                      } catch (error) {
                                        toast.error("Failed to delete task");
                                      }
                                    }}
                                  >
                                    <Trash2 className="w-4 h-4 mr-2" />
                                    Delete
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </TableCell>
                          </TableRow>
                        ))}
                      {tasks.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={8} className="text-center py-12 text-muted-foreground">
                            No tasks yet. Create your first task!
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              </Card>
            </motion.div>
          )}
        </div>
      </main>

      {/* Create/Edit Schedule Dialog */}
      <Dialog open={showScheduleDialog} onOpenChange={setShowScheduleDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">{editingSchedule ? "Edit Recurring Schedule" : "Add Recurring Schedule"}</DialogTitle>
            <DialogDescription>
              {editingSchedule ? "Update the schedule details below." : "Create a task that repeats every week."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Day of Week</label>
                <Select 
                  value={scheduleForm.day_of_week} 
                  onValueChange={(value) => setScheduleForm({ ...scheduleForm, day_of_week: value })}
                >
                  <SelectTrigger data-testid="schedule-day-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Monday">Monday</SelectItem>
                    <SelectItem value="Tuesday">Tuesday</SelectItem>
                    <SelectItem value="Wednesday">Wednesday</SelectItem>
                    <SelectItem value="Thursday">Thursday</SelectItem>
                    <SelectItem value="Friday">Friday</SelectItem>
                    <SelectItem value="Saturday">Saturday</SelectItem>
                    <SelectItem value="Sunday">Sunday</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Time Slot</label>
                <Input
                  placeholder="e.g., 8:00 AM - 9:00 AM"
                  value={scheduleForm.time_slot}
                  onChange={(e) => setScheduleForm({ ...scheduleForm, time_slot: e.target.value })}
                  data-testid="schedule-time-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Title</label>
              <Input
                placeholder="Task title"
                value={scheduleForm.title}
                onChange={(e) => setScheduleForm({ ...scheduleForm, title: e.target.value })}
                data-testid="schedule-title-input"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Description</label>
              <Textarea
                placeholder="Task description (optional)"
                value={scheduleForm.description}
                onChange={(e) => setScheduleForm({ ...scheduleForm, description: e.target.value })}
                data-testid="schedule-description-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Priority</label>
                <Select value={scheduleForm.priority} onValueChange={(value) => setScheduleForm({ ...scheduleForm, priority: value })}>
                  <SelectTrigger data-testid="schedule-priority-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="low">Low</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Assign To</label>
                <Select value={scheduleForm.assigned_to || "unassigned"} onValueChange={(value) => setScheduleForm({ ...scheduleForm, assigned_to: value === "unassigned" ? "" : value })}>
                  <SelectTrigger data-testid="schedule-assignee-select">
                    <SelectValue placeholder="Select employee" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unassigned">Unassigned</SelectItem>
                    {employees.map((emp) => (
                      <SelectItem key={emp.user_id} value={emp.user_id}>{emp.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowScheduleDialog(false)}>Cancel</Button>
            <Button 
              onClick={async () => {
                try {
                  if (editingSchedule) {
                    await axios.patch(`${API}/recurring-schedules/${editingSchedule.schedule_id}`, scheduleForm, { withCredentials: true });
                    toast.success("Schedule updated");
                  } else {
                    await axios.post(`${API}/recurring-schedules`, scheduleForm, { withCredentials: true });
                    toast.success("Schedule created");
                  }
                  setShowScheduleDialog(false);
                  setEditingSchedule(null);
                  fetchData();
                } catch (error) {
                  toast.error(error.response?.data?.detail || "Failed to save schedule");
                }
              }} 
              disabled={!scheduleForm.title || !scheduleForm.time_slot}
              data-testid="save-schedule-btn"
            >
              {editingSchedule ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create/Edit Task Dialog */}
      <Dialog open={showTaskDialog} onOpenChange={setShowTaskDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">{editingTask ? "Edit Task" : "Create New Task"}</DialogTitle>
            <DialogDescription>
              {editingTask ? "Update the task details below." : "Create a task with a specific date, time, and duration."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Title *</label>
              <Input
                placeholder="Task title"
                value={taskForm.title}
                onChange={(e) => setTaskForm({ ...taskForm, title: e.target.value })}
                data-testid="task-title-input"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Description</label>
              <Textarea
                placeholder="Task description (optional)"
                value={taskForm.description}
                onChange={(e) => setTaskForm({ ...taskForm, description: e.target.value })}
                data-testid="task-description-input"
              />
            </div>

            {/* Recurring Task Option */}
            <div className="flex items-center space-x-3 p-3 rounded-lg bg-muted/50 border">
              <Checkbox 
                id="is_recurring" 
                checked={taskForm.is_recurring}
                onCheckedChange={(checked) => setTaskForm({ ...taskForm, is_recurring: checked })}
                data-testid="task-recurring-checkbox"
              />
              <div className="flex-1">
                <Label htmlFor="is_recurring" className="text-sm font-medium cursor-pointer">
                  Repeat this task weekly
                </Label>
                <p className="text-xs text-muted-foreground">Task will be created for every selected day of the week</p>
              </div>
            </div>

            {/* Repeat Day Selection (shown when recurring is checked) */}
            {taskForm.is_recurring && (
              <div className="space-y-2">
                <label className="text-sm font-medium">Repeat Every</label>
                <Select value={taskForm.repeat_day} onValueChange={(value) => setTaskForm({ ...taskForm, repeat_day: value })}>
                  <SelectTrigger data-testid="task-repeat-day-select">
                    <SelectValue placeholder="Select day" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Monday">Monday</SelectItem>
                    <SelectItem value="Tuesday">Tuesday</SelectItem>
                    <SelectItem value="Wednesday">Wednesday</SelectItem>
                    <SelectItem value="Thursday">Thursday</SelectItem>
                    <SelectItem value="Friday">Friday</SelectItem>
                    <SelectItem value="Saturday">Saturday</SelectItem>
                    <SelectItem value="Sunday">Sunday</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">{taskForm.is_recurring ? "Start Date" : "Date *"}</label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal",
                        !taskForm.task_date && !taskForm.is_recurring && "text-muted-foreground"
                      )}
                      data-testid="task-date-picker"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {taskForm.task_date ? format(taskForm.task_date, "MMM d, yyyy") : (taskForm.is_recurring ? "Optional start date" : "Select date")}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={taskForm.task_date}
                      onSelect={(date) => setTaskForm({ ...taskForm, task_date: date })}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Start Time *</label>
                <Select value={taskForm.start_time} onValueChange={(value) => setTaskForm({ ...taskForm, start_time: value })}>
                  <SelectTrigger data-testid="task-time-select">
                    <SelectValue placeholder="Select time" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="06:00">6:00 AM</SelectItem>
                    <SelectItem value="06:30">6:30 AM</SelectItem>
                    <SelectItem value="07:00">7:00 AM</SelectItem>
                    <SelectItem value="07:30">7:30 AM</SelectItem>
                    <SelectItem value="08:00">8:00 AM</SelectItem>
                    <SelectItem value="08:30">8:30 AM</SelectItem>
                    <SelectItem value="09:00">9:00 AM</SelectItem>
                    <SelectItem value="09:30">9:30 AM</SelectItem>
                    <SelectItem value="10:00">10:00 AM</SelectItem>
                    <SelectItem value="10:30">10:30 AM</SelectItem>
                    <SelectItem value="11:00">11:00 AM</SelectItem>
                    <SelectItem value="11:30">11:30 AM</SelectItem>
                    <SelectItem value="12:00">12:00 PM</SelectItem>
                    <SelectItem value="12:30">12:30 PM</SelectItem>
                    <SelectItem value="13:00">1:00 PM</SelectItem>
                    <SelectItem value="13:30">1:30 PM</SelectItem>
                    <SelectItem value="14:00">2:00 PM</SelectItem>
                    <SelectItem value="14:30">2:30 PM</SelectItem>
                    <SelectItem value="15:00">3:00 PM</SelectItem>
                    <SelectItem value="15:30">3:30 PM</SelectItem>
                    <SelectItem value="16:00">4:00 PM</SelectItem>
                    <SelectItem value="16:30">4:30 PM</SelectItem>
                    <SelectItem value="17:00">5:00 PM</SelectItem>
                    <SelectItem value="17:30">5:30 PM</SelectItem>
                    <SelectItem value="18:00">6:00 PM</SelectItem>
                    <SelectItem value="18:30">6:30 PM</SelectItem>
                    <SelectItem value="19:00">7:00 PM</SelectItem>
                    <SelectItem value="19:30">7:30 PM</SelectItem>
                    <SelectItem value="20:00">8:00 PM</SelectItem>
                    <SelectItem value="20:30">8:30 PM</SelectItem>
                    <SelectItem value="21:00">9:00 PM</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Duration</label>
                <Select value={String(taskForm.duration)} onValueChange={(value) => setTaskForm({ ...taskForm, duration: parseInt(value) })}>
                  <SelectTrigger data-testid="task-duration-select">
                    <SelectValue placeholder="Select duration" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="30">30 minutes</SelectItem>
                    <SelectItem value="60">1 hour</SelectItem>
                    <SelectItem value="90">1.5 hours</SelectItem>
                    <SelectItem value="120">2 hours</SelectItem>
                    <SelectItem value="180">3 hours</SelectItem>
                    <SelectItem value="240">4 hours</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Priority</label>
                <Select value={taskForm.priority} onValueChange={(value) => setTaskForm({ ...taskForm, priority: value })}>
                  <SelectTrigger data-testid="task-priority-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="low">Low</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Assign To</label>
              <Select value={taskForm.assigned_to || "unassigned"} onValueChange={(value) => setTaskForm({ ...taskForm, assigned_to: value === "unassigned" ? "" : value })}>
                <SelectTrigger data-testid="task-assignee-select">
                  <SelectValue placeholder="Select employee" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="unassigned">Unassigned</SelectItem>
                  {employees.map((emp) => (
                    <SelectItem key={emp.user_id} value={emp.user_id}>{emp.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTaskDialog(false)}>Cancel</Button>
            <Button 
              onClick={async () => {
                try {
                  const taskData = {
                    title: taskForm.title,
                    description: taskForm.description,
                    priority: taskForm.priority,
                    assigned_to: taskForm.assigned_to || null,
                    task_date: taskForm.task_date ? format(taskForm.task_date, "yyyy-MM-dd") : null,
                    start_time: taskForm.start_time,
                    duration: taskForm.duration,
                    is_recurring: taskForm.is_recurring,
                    repeat_day: taskForm.is_recurring ? taskForm.repeat_day : null
                  };

                  if (editingTask) {
                    await axios.patch(`${API}/tasks/${editingTask.task_id}`, taskData, { withCredentials: true });
                    toast.success("Task updated");
                  } else {
                    await axios.post(`${API}/tasks`, taskData, { withCredentials: true });
                    toast.success(taskForm.is_recurring ? "Recurring task created" : "Task created");
                  }
                  setShowTaskDialog(false);
                  setEditingTask(null);
                  fetchData();
                } catch (error) {
                  toast.error(error.response?.data?.detail || "Failed to save task");
                }
              }} 
              disabled={!taskForm.title || (!taskForm.is_recurring && !taskForm.task_date) || !taskForm.start_time}
              data-testid="save-task-btn"
            >
              {editingTask ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Suspend User Confirmation Dialog */}
      <AlertDialog open={showSuspendDialog} onOpenChange={setShowSuspendDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Suspend User
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to suspend <strong>{userToSuspend?.name}</strong>? 
              They will not be able to log in until reactivated.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setUserToSuspend(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={() => handleSuspendUser(userToSuspend?.user_id)}
              className="bg-amber-500 hover:bg-amber-600"
            >
              Suspend
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete User Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-destructive">
              <Trash2 className="w-5 h-5" />
              Delete User
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to permanently delete <strong>{userToDelete?.name}</strong>? 
              This action cannot be undone. All tasks assigned to this user will be unassigned.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setUserToDelete(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={() => handleDeleteUser(userToDelete?.user_id)}
              className="bg-destructive hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Assign Projects Dialog */}
      <Dialog open={showProjectAssignDialog} onOpenChange={setShowProjectAssignDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FolderKanban className="w-5 h-5 text-primary" />
              Assign QA Projects
            </DialogTitle>
            <DialogDescription>
              Assign QA projects to <strong>{selectedEmployee?.name}</strong>. 
              They will only see calls from provinces in their assigned projects.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {qaProjects.length > 0 ? (
              <div className="space-y-2">
                {qaProjects.map((project) => (
                  <div 
                    key={project.project_id}
                    className={cn(
                      "flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors",
                      selectedProjectIds.includes(project.project_id)
                        ? "bg-primary/10 border-primary"
                        : "hover:bg-muted"
                    )}
                    onClick={() => {
                      const isSelected = selectedProjectIds.includes(project.project_id);
                      if (isSelected) {
                        setSelectedProjectIds(selectedProjectIds.filter(id => id !== project.project_id));
                      } else {
                        setSelectedProjectIds([...selectedProjectIds, project.project_id]);
                      }
                    }}
                  >
                    <Checkbox 
                      checked={selectedProjectIds.includes(project.project_id)}
                      className="mt-1"
                    />
                    <div className="flex-1">
                      <p className="font-medium">{project.name}</p>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {project.provinces?.map(prov => (
                          <Badge key={prov} variant="secondary" className="text-xs">
                            {prov}
                          </Badge>
                        ))}
                      </div>
                      {project.supervisor_name && (
                        <p className="text-xs text-muted-foreground mt-1">
                          Supervisor: {project.supervisor_name}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="py-6 text-center text-muted-foreground">
                <FolderKanban className="w-10 h-10 mx-auto mb-2 opacity-50" />
                <p>No projects created yet</p>
                <p className="text-sm">Go to QA Calls → Projects to create one</p>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowProjectAssignDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpdateUserProjects} disabled={qaProjects.length === 0}>
              Save Assignment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
